import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

// The modules, not the `utils` barrel: the barrel re-exports `platform-logo`,
// which reads the design system and cannot load in jsdom, and this hook is
// rendered there through `useModalNavigation` (use-modal-navigation.test.tsx).
import type { PlatformChannel } from '../../utils/map-connection-to-row';
import { createSolutionResolver } from '../../utils/resolve-solution-by-bot-id';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { buildChannelRoute } from './channel-route';

/**
 * Where a channel pill leads, or `null` when it leads nowhere — WITHOUT going
 * there. The route decision is `buildChannelRoute`; the channel → solution
 * lookup inside it is `createSolutionResolver`, shared with the "Channels Using
 * It" grouping. Split from the navigation so a caller can decide what to do
 * BEFORE leaving, and only when it will actually leave (the Edit modal closes
 * itself first; see `closeThenNavigate`).
 */
export const useChannelRoute = () => {
  const projectId = useProStudioCtxSelector((ctx) => ctx.project?.id);
  const solutions = useProStudioCtxSelector((ctx) => ctx.solutions);
  const resolveSolution = useMemo(
    () => createSolutionResolver(solutions),
    [solutions]
  );

  return useCallback(
    (channel: PlatformChannel) =>
      buildChannelRoute(channel, projectId, resolveSolution),
    [projectId, resolveSolution]
  );
};

/**
 * Navigates to a channel's settings page from a project-settings table, and
 * does nothing when the channel has no route (see `useChannelRoute`).
 */
export const useGoToChannel = () => {
  const navigate = useNavigate();
  const channelRoute = useChannelRoute();

  return useCallback(
    (channel: PlatformChannel) => {
      const route = channelRoute(channel);
      if (route) navigate(route);
    },
    [navigate, channelRoute]
  );
};
